import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { isPast } from 'date-fns';
import type { ReactNode } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Box } from '@sorare/core/src/atoms/icons/Box';
import { Whistle } from '@sorare/core/src/atoms/icons/Whistle';
import { iconCalendar } from '@sorare/core/src/atoms/icons/system/iconCalendar.ts';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconShirt } from '@sorare/core/src/atoms/icons/system/iconShirt.ts';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS } from '@sorare/core/src/atoms/typography';
import { TimelineRow } from '@sorare/core/src/components/TimelineRow';
import { groupBy, sortBy, uniqueBy } from '@sorare/core/src/lib/arrays';
import { glossary } from '@sorare/core/src/lib/glossary';

import { FixtureTimelineGame } from './FixtureTimelineGame/index.tsx';
import { MainItemContent } from './MainItemContent/index.tsx';
import type {
  FixtureTimeline_composeTeamBenchObjectInterface,
  FixtureTimeline_game,
  FixtureTimeline_so5Leaderboard,
  FixtureTimeline_so5Lineup,
} from './__generated__/index.graphql.ts';

const Wrapper = styled(Vertical)`
  isolation: isolate;
  --dot-width: calc(var(--quadruple-unit));
`;

const AppearanceImg = styled.img`
  width: var(--quadruple-unit);
`;

const Cards = styled(Horizontal)`
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Date = styled(BodyS)`
  font-variant-numeric: tabular-nums;

  &.hide {
    visibility: hidden;
  }
`;

type Props = {
  so5Leaderboard: FixtureTimeline_so5Leaderboard;
  so5Lineup?: FixtureTimeline_so5Lineup | null;
  // read next games info from bench objects if available
  // useful for the case where lineup is not saved yet
  benchObjects?: FixtureTimeline_composeTeamBenchObjectInterface[];
  onClickGame?: (gameId: string) => void;
  rewardsTitle?: ReactNode;
};

export const FixtureTimeline = ({
  so5Leaderboard,
  so5Lineup,
  benchObjects,
  onClickGame,
  rewardsTitle,
}: Props) => {
  const { so5Fixture, cutOffDate } = so5Leaderboard;
  const { formatDate } = useIntl();

  const benchObjectGames =
    benchObjects?.flatMap(
      benchObject => benchObject.anyPlayer?.anyGamesForLeaderboard
    ) || [];

  const games = sortBy(
    game => game?.date || '',
    uniqueBy(
      game => game?.id || '',
      benchObjectGames.length > 0
        ? benchObjectGames
        : so5Lineup?.so5Appearances
            .flatMap(appearance => appearance.eligiblePlayerGameScores)
            .map(gameScore => gameScore.anyGame) || []
    )
  ).filter(Boolean);

  const gamesByDate = groupBy(games, game =>
    formatDate(game.date, { format: 'yyyy-MM-dd' })
  );
  const lineupSubmitted = !!so5Lineup && !so5Lineup.draft;

  return (
    <Wrapper
      gap={3}
      style={{ '--progress-color': 'var(--c-score-high)' } as CSSProperties}
    >
      <Vertical gap={0}>
        <TimelineRow
          label={
            lineupSubmitted ? (
              <FormattedMessage defaultMessage="Lineup is set" />
            ) : (
              <FormattedMessage defaultMessage="Set your lineup" />
            )
          }
          progress={lineupSubmitted ? 1 : 0}
          dot={D => (
            <D completed={lineupSubmitted}>
              {lineupSubmitted ? (
                <FontAwesomeIcon icon={iconCheck} />
              ) : (
                <FontAwesomeIcon icon={iconShirt} />
              )}
            </D>
          )}
        />
        {!lineupSubmitted && (
          // display gameweek starts if lineup not set to avoid an empty timeline
          <TimelineRow
            date={cutOffDate}
            icon={<Whistle />}
            progress={isPast(cutOffDate) ? 1 : 0}
          >
            <Horizontal>
              <Date color="var(--c-nd-700)">
                <FormattedDate
                  value={cutOffDate}
                  hour="2-digit"
                  minute="2-digit"
                />
              </Date>
              <MainItemContent>
                <BodyM>
                  <FormattedMessage defaultMessage="Gameweek starts" />
                </BodyM>
              </MainItemContent>
            </Horizontal>
          </TimelineRow>
        )}
        {Object.entries(gamesByDate).map(([date, gamesList]) => (
          <TimelineRow
            key={date}
            date={gamesList?.at(0)?.date}
            icon={<FontAwesomeIcon icon={iconCalendar} />}
            progress={
              (gamesList?.reduce(
                (acc, cur) => (isPast(cur.date) ? acc + 1 : acc),
                0
              ) || 0) / (gamesList?.length || 1)
            }
          >
            <Vertical gap={2}>
              {gamesList?.map(game => {
                const correspondingBenchObjects = benchObjects?.length
                  ? benchObjects?.filter(benchObject =>
                      benchObject.anyPlayer?.anyGamesForLeaderboard.some(
                        benchObjectGame => benchObjectGame?.id === game.id
                      )
                    )
                  : so5Lineup?.so5Appearances.filter(appearance =>
                      appearance.eligiblePlayerGameScores.some(
                        gameScore => gameScore.anyGame?.id === game.id
                      )
                    );
                return (
                  <SBHorizontal key={game.id} gap={8}>
                    <ConditionalWrapper
                      wrap={!!onClickGame}
                      Wrapper={ButtonBase}
                      props={{ onClick: () => onClickGame?.(game.id) }}
                    >
                      <FixtureTimelineGame game={game} />
                    </ConditionalWrapper>
                    <Cards>
                      {correspondingBenchObjects?.map(appearance => (
                        <AppearanceImg
                          src={appearance.pictureUrl ?? undefined}
                          alt=""
                          key={appearance.id}
                        />
                      ))}
                    </Cards>
                  </SBHorizontal>
                );
              })}
            </Vertical>
          </TimelineRow>
        ))}
        <TimelineRow
          date={so5Fixture.endDate}
          isLastRow
          icon={<Box />}
          progress={
            isPast(
              so5Fixture.expectedRewardsDistributionDate || so5Fixture.endDate
            )
              ? 1
              : 0
          }
        >
          <Horizontal>
            <Date
              color="var(--c-nd-700)"
              className={classNames({
                hide: !so5Fixture.expectedRewardsDistributionDate,
              })}
            >
              <FormattedDate
                value={
                  so5Fixture.expectedRewardsDistributionDate ||
                  so5Fixture.endDate
                }
                hour="2-digit"
                minute="2-digit"
              />
            </Date>
            <MainItemContent>
              <BodyM>
                {rewardsTitle || <FormattedMessage {...glossary.rewards} />}
              </BodyM>
            </MainItemContent>
          </Horizontal>
        </TimelineRow>
      </Vertical>
    </Wrapper>
  );
};

const gameFragment = gql`
  fragment FixtureTimeline_game on AnyGameInterface {
    id
    date
    ...FixtureTimelineGame_game
  }
  ${FixtureTimelineGame.fragments.game}
` as TypedDocumentNode<FixtureTimeline_game>;

FixtureTimeline.fragments = {
  so5Leaderboard: gql`
    fragment FixtureTimeline_so5Leaderboard on So5Leaderboard {
      slug
      cutOffDate
      so5Fixture {
        slug
        endDate
        expectedRewardsDistributionDate
      }
    }
  ` as TypedDocumentNode<FixtureTimeline_so5Leaderboard>,
  composeTeamBenchObjectInterface: gql`
    fragment FixtureTimeline_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      pictureUrl
      anyPlayer {
        slug
        anyGamesForLeaderboard(so5LeaderboardSlug: $so5LeaderboardSlug) {
          id
          ...FixtureTimeline_game
        }
      }
    }
    ${gameFragment}
  ` as TypedDocumentNode<FixtureTimeline_composeTeamBenchObjectInterface>,
  // oxlint-disable-next-line sorare/no-unused-fragment-fields
  so5Lineup: gql`
    fragment FixtureTimeline_so5Lineup on So5Lineup {
      id
      draft
      so5Appearances {
        id
        pictureUrl
        eligiblePlayerGameScores {
          id
          anyGame {
            id
            ...FixtureTimeline_game
          }
        }
      }
    }
    ${gameFragment}
  ` as TypedDocumentNode<FixtureTimeline_so5Lineup>,
};
